import { faAnglesUpDown } from "@fortawesome/pro-solid-svg-icons/faAnglesUpDown";
import { Box, CardContent, Grid, Stack, Typography } from "@mui/material";
import { MprcButtonDropdown, MprcCard, MprcContainer, MprcFormSubHeader, MprcIconButton, MprcIconFa, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonAdd, ButtonEdit, ButtonFinish, ButtonSave } from "components/buttons";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconButtonEdit } from "components/iconButtons";
import { IMenuItem } from "components/MprcButtonDropdown";
import { MprcNotice } from "components/MprcNotice";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { DevPlanCreationMode, DevPlanItemsResponse, DevPlansGroupsDto, DevPlanViewDto } from "generated/CoreApi_v1";
import TzUtils from "lib/TzUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { NIL } from "uuid";
import { PublicationSelectorDrawer } from "views/knowledge/publication/drawers/PublicationSelectorDrawer";
import { DevPlanDiscussionBox } from "views/lms/components/discussion/DevPlanDiscussionsBox";
import { TrainingSelectorDrawer } from "views/lms/devPlan/drawers/TrainingSelectorDrawer";
import { DevPlanHeader } from "views/lms/devPlan/Home/components/DevPlanHeader";
import { EditDevPlanGroupDialog } from "views/lms/devPlan/Home/Edit/EditDevPlanGroup";
import { DevPlanCardItem } from "views/lms/devPlan/Home/Edit/EditDevPlanView/DevPlanCardItem";
import { DevPlanConfirmEnrollmentDialog } from "views/lms/devPlan/Home/Edit/EditDevPlanView/DevPlanConfirmEnrollment";
import { DevPlanReorderGroupsDialog } from "views/lms/devPlan/Home/Edit/EditDevPlanView/DevPlanReorderGroupsDialog";
import { DevPlanViewHeader } from "views/lms/devPlan/Home/Edit/EditDevPlanView/DevPlanViewHeader";
import { EditDevPlanViewFooter } from "views/lms/devPlan/Home/Edit/EditDevPlanView/EditDevPlanViewFooter";
import { CreateDevPlanActivity } from "views/lms/devPlan/publication/CreateActivity";

export enum ActivityOptions {
    Training = 1,
    Post = 2,
    Activity = 3,
}

export enum OperationMode {
    Edit = 1,
    View = 2,
    Readonly = 3,
}

interface IProp {
    mode?: OperationMode;
}

export const EditDevPlanView: React.FC<IProp> = (props) => {
    const { devPlanId = NIL } = useParams();
    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();

    const [dialog, setDialog] = useState<React.ReactNode>(undefined);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<DevPlanViewDto>();
    const [items, setItems] = useState<DevPlanItemsResponse>();
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);
    const [mode, setMode] = useState<OperationMode>();

    const canPublish = viewData && viewData.publishedOn == null && items && items.groups.length > 0 && items.groups.every(g => g.items.length > 0);

    ///
    const publish = async () => {
        setPosting(true);

        try {
            const result = await api.v1.devPlan.publish({ devPlanId: viewData!.devPlanId });
            if (result.data) {
                if (viewData?.actions.canView) {
                    setMode(OperationMode.View);
                }
                else {
                    navigate(-1);
                }
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const markApproveReview = async (approved: boolean) => {
        setPosting(true);

        try {
            const result = await api.v1.devPlan.markApprovedDevPlan({ devPlanId: viewData!.devPlanId, approved });
            if (result.data) {
                loadData();
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const markReadonlyReview = async (readonly: boolean) => {
        setPosting(true);

        try {
            const result = await api.v1.devPlan.markReadonlyDevPlan({ devPlanId: viewData!.devPlanId, readonly });
            if (result.data) {
                loadData();
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const Header = () => {
        var action: React.ReactNode = undefined;

        if (viewData?.publishedOn == null) {
            action = <ButtonSave
                key="btnPublish"
                rounded
                variant="text"
                text={t("DevPlan.Edit.View.PublishButtonText")}
                size="large"
                disabled={posting || loading || !canPublish}
                onClick={() => publish()}
            />;
        } else if (viewData.actions.canEdit && mode == OperationMode.View) {
            action = <ButtonEdit
                key="ButtonEdit"
                rounded
                variant="text"
                disabled={posting || loading}
                onClick={() => setMode(OperationMode.Edit)}
            />;
        } else if (viewData.actions.canView && mode == OperationMode.Edit) {
            action = <ButtonFinish
                key="ButtonFinish"
                rounded
                variant="contained"
                disabled={posting || loading}
                onClick={() => setMode(OperationMode.View)}
            />;
        }

        return (
            <DevPlanHeader
                otherAction={action}
                title={t("DevPlan.Edit.View.Title")}
            />
        );
    };

    ///
    const getAddItemsOptions = useCallback(
        (): Array<IMenuItem> => {
            var items: Array<IMenuItem> = [];

            items.push({ text: t("DevPlan.Edit.View.ActivityOptions.Training"), value: ActivityOptions.Training });
            items.push({ text: t("DevPlan.Edit.View.ActivityOptions.Post"), value: ActivityOptions.Post });
            items.push({ text: t("DevPlan.Edit.View.ActivityOptions.Activity"), value: ActivityOptions.Activity });

            return items;
        }
        , []
    );

    ///
    const handleEditGroup = (devPlanGroupId?: string) => {
        setDialog(<EditDevPlanGroupDialog
            devPlanId={devPlanId}
            devPlanGroupId={devPlanGroupId ?? null}
            onClose={(success) => {
                if (success) loadData();
                setDialog(undefined);
            }}
        />);
    };

    ///
    const handleOrderGroups = () => {
        setDialog(
            <DevPlanReorderGroupsDialog
                devPlanId={devPlanId}
                subtitle={viewData?.name}
                onClose={success => {
                    if (success) loadData();
                    setDialog(null);
                }}
            />);
    };

    ///
    const addTraining = async (group: DevPlansGroupsDto, trainingId: string, ignoreApprovedEnrollment: boolean) => {
        setPosting(true);

        try {
            const result = await api.v1.devPlan.createTrainingActivity({ devPlanGroupId: group.devPlanGroupId, trainingId, ignoreApprovedEnrollment });
            if (result.data) loadData();
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const checkEnrollmentAndAddTraining = async (group: DevPlansGroupsDto, trainingId: string) => {
        setLoading(true);
        try {
            const result = await api.v1.devPlan.checkTrainingApproved({ userId: viewData!.userId, trainingId });
            setLoading(false);
            if (!result.data) {
                addTraining(group, trainingId, false);
            } else {
                setDialog(
                    <DevPlanConfirmEnrollmentDialog
                        open
                        severity="question"
                        message={t(viewData?.creationMode == DevPlanCreationMode.Manager ? "DevPlan.Edit.View.EnrollManagerConfirm" : "DevPlan.Edit.View.EnrollOwnUserConfirm")}
                        options={[
                            { value: "addCompleted", label: t("DevPlan.Edit.View.EnrollConfirmYes") },
                            { value: "addEnrollEmpty", label: t("DevPlan.Edit.View.EnrollEmptyConfirm") },
                        ]}
                        cancelText={t("DevPlan.Edit.View.EnrollConfirmNo")}
                        onClose={(option) => {
                            if (option) {
                                if (option.value == "addCompleted") {
                                    addTraining(group, trainingId, false);
                                } else if (option.value == "addEnrollEmpty") {
                                    addTraining(group, trainingId, true);
                                }
                            }
                            setDialog(null);
                        }}
                    />
                );
            }
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const addPost = async (group: DevPlansGroupsDto, publicationId: string) => {
        setPosting(true);

        try {
            const result = await api.v1.devPlan.createPublicationActivity({ devPlanGroupId: group.devPlanGroupId, publicationId });
            if (result.data) loadData();
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const handleAddItemAction = (action: ActivityOptions, group: DevPlansGroupsDto) => {
        switch (action) {
            case ActivityOptions.Training:
                setDialog(<TrainingSelectorDrawer
                    userId={viewData!.userId}
                    onClose={(trainings) => {
                        if (trainings && trainings.length == 1) {
                            if (viewData?.publishedOn == null) {
                                addTraining(group, trainings[0].trainingId, false);
                            } else {
                                checkEnrollmentAndAddTraining(group, trainings[0].trainingId);
                            }
                        }
                        setDialog(undefined);
                    }}
                />);
                break;
            case ActivityOptions.Post:
                setDialog(<PublicationSelectorDrawer
                    userId={viewData!.userId}
                    onlyPostPublications
                    onClose={(publications) => {
                        if (publications && publications.length == 1) {
                            addPost(group, publications[0].publicationId);
                        }
                        setDialog(undefined);
                    }}
                />);
                break;
            case ActivityOptions.Activity:
                setDialog(<CreateDevPlanActivity
                    devPlanGroupId={group.devPlanGroupId}
                    onClose={
                        (success) => {
                            if (success) loadData();
                            setDialog(null);
                        }
                    }
                />);
                break;
        }
    };

    ///
    const loadData = async () => {
        setLoading(true);
        try {
            const response = await api.v1.devPlan.getDevPlanView({ devPlanId: devPlanId! });
            setViewData(response.data);

            const actions = response.data.actions;

            if (actions.canEdit || actions.canView || actions.accessReadonly) {
                const result = await api.v1.devPlan.getDevPlanItems({ devPlanId: devPlanId!, browserTimeZoneId: TzUtils.browserTimezone(), editMode: mode == OperationMode.Edit && actions.canEdit });
                setItems(result.data);
            }

            if (mode == undefined) {
                if (props.mode == OperationMode.Edit && actions.canEdit)
                    setMode(OperationMode.Edit);
                else if (props.mode == OperationMode.View && actions.canView)
                    setMode(OperationMode.View);
                else if (actions.accessReadonly)
                    setMode(OperationMode.Readonly);
                else
                    setMode(actions.canView && response.data.publishedOn != null ? OperationMode.View : OperationMode.Edit);
            }
        }
        catch (e) {
            setItems(undefined);
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {

        if (devPlanId != NIL && !loading) loadData();

    }, [location.pathname, mode]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const renderGroup = (group: DevPlansGroupsDto) => {
        const buildGroupSubtitle = () => {
            var items: React.ReactNode[] = [];

            if (group.topicName) {
                items.push(group.topicName);
            }

            if (group.description) {
                items.push(group.description);
            }

            return <Typography variant="caption" component="p"><MprcJoinComponents components={items} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
        };

        return (
            <Stack direction="column">
                <MprcFormSubHeader
                    title={
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h3" component="p" color="muted">
                                    {group.name}
                                </Typography>
                                {mode == OperationMode.Edit && viewData && viewData.actions.canEdit &&
                                    <IconButtonEdit
                                        size="small"
                                        onClick={() => handleEditGroup(group.devPlanGroupId)}
                                    />
                                }
                            </Stack>
                        </Box>

                    }
                    subtitle={buildGroupSubtitle()}
                />
            </Stack>
        );
    };

    if (devPlanId === NIL) {
        return (t("DevPlan.Edit.View.DevPlanNotFound"));
    }

    return (
        <Box height="100%">
            <MprcRequestIndicator visible={loading || posting} type={loading ? "request" : "post"} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />

            {dialog}

            {viewData && mode &&
                <Grid container direction="column">
                    <Grid width="100%">
                        {Header()}
                    </Grid>
                    {
                        viewData.publishedOn == null && viewData.creationMode == DevPlanCreationMode.Manager &&
                        (
                            <Grid width="100%">
                                <MprcNotice
                                    elevation="small"
                                    size="small"
                                    items={[
                                        {
                                            text:
                                                <>
                                                    {t("DevPlan.Edit.View.DraftMessageManager_L1")}<br />
                                                    {t("DevPlan.Edit.View.DraftMessageManager_L2")}
                                                </>
                                        }]}
                                />
                            </Grid>
                        )
                    }

                    {
                        viewData.publishedOn == null && viewData.creationMode == DevPlanCreationMode.OwnUser &&
                        (
                            <Grid width="100%">
                                <MprcNotice
                                    elevation="small"
                                    size="small"
                                    items={[
                                        {
                                            text:
                                                <>
                                                    {t("DevPlan.Edit.View.DraftMessageUser_L1")}<br />
                                                    {t("DevPlan.Edit.View.DraftMessageUser_L2")}
                                                </>
                                        }]}
                                />
                            </Grid>
                        )
                    }

                    {
                        viewData.actions.canReview &&
                        (
                            <Grid width="100%">
                                <MprcContainer>
                                    <MprcCard paperColor="notice" sx={{ paddingX: 2 }}>
                                        <CardContent>
                                            <MprcFormSubHeader
                                                title={t("DevPlan.Edit.View.ReviewMessageTitle")}
                                                subtitle={
                                                    <div>
                                                        <div>{t("DevPlan.Edit.View.ReviewMessageSubtitle_L1")}</div>
                                                        <div>{t("DevPlan.Edit.View.ReviewMessageSubtitle_L2")}</div>
                                                    </div>
                                                }
                                            />
                                            <Box marginTop={2}>
                                                <MprcSwitch
                                                    checked={viewData.isApproved}
                                                    onChange={(e) => markApproveReview(e.target.checked)}
                                                    label={t("DevPlan.Edit.View.ApprovedLabel")}
                                                />
                                                <MprcSwitch
                                                    checked={viewData.isReadonly}
                                                    disabled={!viewData.isApproved}
                                                    onChange={(e) => markReadonlyReview(e.target.checked)}
                                                    label={t("DevPlan.Edit.View.ReadonlyLabel")}
                                                />
                                            </Box>
                                        </CardContent>
                                    </MprcCard>
                                </MprcContainer>
                            </Grid>
                        )
                    }

                    <Grid width="100%">
                        <DevPlanViewHeader viewData={viewData} mode={mode} onChange={() => loadData()} />
                    </Grid>
                    <Grid width="100%">
                        <MprcContainer>
                            <MprcCard>
                                {mode == OperationMode.Edit && viewData.actions.canEdit &&
                                    <MprcCardFilterBox>
                                        <Grid container alignItems="center" width="100%" spacing={1}>
                                            <Grid size="grow">
                                                <ButtonAdd
                                                    size="large"
                                                    color="inherit"
                                                    onClick={() => handleEditGroup()}
                                                    text={t("DevPlan.Edit.View.NewGroupButtonText")}
                                                    title={t("DevPlan.Edit.View.NewGroupButtonTitle")}
                                                />
                                            </Grid>
                                            <Grid>
                                                <MprcIconButton
                                                    onClick={() => handleOrderGroups()}
                                                    title={t("DevPlan.Edit.View.OrderGroupTooltip")}
                                                >
                                                    <MprcIconFa icon={faAnglesUpDown} />
                                                </MprcIconButton>
                                            </Grid>
                                        </Grid>
                                    </MprcCardFilterBox>
                                }

                                <CardContent>
                                    <MprcContainer>
                                        {
                                            items?.groups.length === 0 &&
                                            <Typography variant="body1" color="muted">
                                                {t("DevPlan.Edit.View.NoGroupsMessage")}
                                            </Typography>
                                        }
                                        {
                                            items?.groups.map((group) => {
                                                return (
                                                    <Grid container direction="column" key={group.devPlanGroupId} marginBottom={3}>
                                                        <Grid>
                                                            {renderGroup(group)}
                                                        </Grid>
                                                        {
                                                            group.items.map((item) => {
                                                                return (
                                                                    <Grid key={item.card.uniqueId} marginTop={3}>
                                                                        <Box key={item.devPlanItemId}>
                                                                            <DevPlanCardItem item={item} readonly={viewData.archivedOn != null} onReload={() => loadData()} />
                                                                        </Box>
                                                                    </Grid>
                                                                );
                                                            })
                                                        }
                                                        {
                                                            viewData.actions.canEdit && mode == OperationMode.Edit &&
                                                            <Grid marginTop={2} marginLeft={4}>
                                                                <MprcButtonDropdown
                                                                    button={ButtonAdd}
                                                                    text={t("DevPlan.Edit.View.NewActivityButtonText")}
                                                                    buttonColor="highlight"
                                                                    items={getAddItemsOptions()}
                                                                    onChange={(option) => handleAddItemAction(option, group)}
                                                                />
                                                            </Grid>
                                                        }
                                                    </Grid>
                                                );
                                            })
                                        }
                                    </MprcContainer>
                                </CardContent>
                            </MprcCard>
                        </MprcContainer>
                    </Grid>
                    {
                        viewData.publishedOn != null &&
                        <Grid width="100%">
                            <MprcContainer>
                                <MprcCard>
                                    <CardContent>
                                        <MprcContainer>
                                            <MprcFormSubHeader
                                                title={t("DevPlan.Edit.View.CommentTitle")}
                                                marginX={1}
                                            />
                                        </MprcContainer>
                                        <DevPlanDiscussionBox
                                            devPlanId={viewData.devPlanId}
                                            readOnly={viewData.actions.accessReadonly}
                                            title={t("DevPlan.Edit.View.CommentTitle")}
                                        />
                                    </CardContent>
                                </MprcCard>
                            </MprcContainer>
                        </Grid>
                    }
                    <Grid width="100%">
                        <MprcContainer>
                            <EditDevPlanViewFooter
                                devplanId={devPlanId}
                                name={viewData.name}
                                recordInfo={viewData.recordInfo}
                            />
                        </MprcContainer>
                    </Grid>
                </Grid>
            }
        </Box>
    );
};
